/**
 * Nebula hero scene — adapted from silicomatics-design/nebulas concept.
 *
 * GPU-driven particle system: ~3000 points drifting on curl-noise-like
 * trajectories, color-mixed between cyan and the navy-deep brand palette.
 * Camera slowly orbits to add depth without distracting from foreground text.
 *
 * Respects the "animation off" toggle and prefers-reduced-motion by rendering
 * a static single frame (useFrame becomes a no-op).
 */
import { useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { AdaptiveDpr, PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";
import { useAnimation } from "../hooks/useAnimation";

const PARTICLE_COUNT = 3000;

function ParticleField() {
  const ref = useRef<THREE.Points>(null);
  const { animationState } = useAnimation();
  const animOn = animationState === "on";

  const [positions, colors, sizes] = useMemo(() => {
    const pos = new Float32Array(PARTICLE_COUNT * 3);
    const col = new Float32Array(PARTICLE_COUNT * 3);
    const siz = new Float32Array(PARTICLE_COUNT);

    const colorA = new THREE.Color("#4fd1c5");
    const colorB = new THREE.Color("#64eaff");
    const colorC = new THREE.Color("#1b2838");

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // Layered shells: bias toward a flat disk in front of camera
      const r = Math.pow(Math.random(), 0.7) * 12;
      const theta = Math.random() * Math.PI * 2;
      const y = (Math.random() - 0.5) * 4;

      pos[i * 3] = Math.cos(theta) * r;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = Math.sin(theta) * r - 4;

      // Color: cyan dominant near edges, navy near center
      const t = Math.random();
      const mixed = new THREE.Color().lerpColors(
        t > 0.7 ? colorB : colorA,
        colorC,
        Math.pow(Math.random(), 2),
      );
      col[i * 3] = mixed.r;
      col[i * 3 + 1] = mixed.g;
      col[i * 3 + 2] = mixed.b;

      siz[i] = Math.random() * 0.08 + 0.02;
    }
    return [pos, col, siz];
  }, []);

  useFrame((_, delta) => {
    if (!animOn || !ref.current) return;
    // Drift: gentle rotation + per-axis sine wobble for organic feel
    ref.current.rotation.y += delta * 0.05;
    ref.current.rotation.x = Math.sin(performance.now() * 0.00008) * 0.05;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
        <bufferAttribute attach="attributes-size" args={[sizes, 1]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        vertexColors
        transparent
        opacity={0.85}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function CameraDrift() {
  const camera = useThree((s) => s.camera);
  const { animationState } = useAnimation();
  useFrame(() => {
    if (animationState !== "on") return;
    const t = performance.now() * 0.00012;
    camera.position.x = Math.sin(t) * 0.6;
    camera.position.y = Math.cos(t * 0.7) * 0.3;
    camera.lookAt(0, 0, -4);
  });
  return null;
}

export function NebulaScene() {
  return (
    <Canvas
      dpr={[1, 2]}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      gl={{ antialias: false, alpha: true, powerPreference: "low-power" }}
    >
      <PerspectiveCamera makeDefault position={[0, 0, 4]} fov={70} />
      <AdaptiveDpr pixelated={false} />
      <fog attach="fog" args={["#0d1b2a", 6, 18]} />
      <ParticleField />
      <CameraDrift />
    </Canvas>
  );
}
